import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import AOS from "aos";
import "aos/dist/aos.css";
import Header from "./Header";
import Footer from "./footer";
import WhatsAppButton from "./WhatsAppButton";
import SeoHead from "./SeoHead";
import {
  createBreadcrumbList,
  createFaqPageSchema,
  createHowToSchema,
  createServiceSchema,
} from "../utils/seo";

export const SITE_URL = "https://qbits9.com";

export const buildServiceSchemas = ({
  name,
  description,
  path,
  serviceType,
  faqs = [],
  breadcrumbItems = [],
  howToSteps = [],
  imageUrl,
}) => {
  const breadcrumbs =
    breadcrumbItems.length > 0
      ? breadcrumbItems
      : [
          { name: "Home", path: "/" },
          { name: "Services", path: "/#services" },
          { name, path },
        ];

  const schemas = [
    createServiceSchema({
      name,
      description,
      path,
      serviceType,
      imageUrl,
    }),
    createFaqPageSchema({
      faqs,
      path,
      name,
      description,
    }),
    createBreadcrumbList(breadcrumbs),
  ];

  if (howToSteps.length > 0) {
    schemas.push(
      createHowToSchema({
        name,
        description,
        path,
        steps: howToSteps,
      }),
    );
  }

  return schemas;
};

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm text-slate-500 font-poppins">
    {items.map((item, index) => (
      <span key={item.path || item.to || item.label} className="flex items-center gap-2">
        {index > 0 ? <span>/</span> : null}
        {item.to ? (
          <Link to={item.to} className="transition hover:text-[#FF6B00]">
            {item.label}
          </Link>
        ) : (
          <span className="text-slate-900">{item.label}</span>
        )}
      </span>
    ))}
  </nav>
);

export const QuickAnswerBox = ({ label, answer, className = "" }) => (
  <div className={`rounded-[28px] border border-[#FF6B0020] bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.05)] ${className}`}>
    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#FF6B00]">{label}</p>
    <p className="quick-answer-text mt-4 text-base leading-8 text-slate-700 font-poppins">{answer}</p>
  </div>
);

export const PeopleAlsoAsk = ({ items }) => {
  const [openIndex, setOpenIndex] = React.useState(0);

  const toggleItem = (index) => {
    if (!items || items.length === 0) return;
    if (openIndex === index) {
      setOpenIndex((index + 1) % items.length);
    } else {
      setOpenIndex(index);
    }
  };

  return (
    <section className="bg-white">
      <div className="mt-6 space-y-4">
        {items.map((item, index) => (
          <article
            key={item.question}
            data-aos="fade-up"
            data-aos-delay={index * 100}
            className="rounded-[18px] border border-slate-200 bg-white p-4 shadow-[0_20px_50px_rgba(15,23,42,0.04)] cursor-pointer hover:border-primary/20 transition-colors duration-300"
            onClick={() => toggleItem(index)}
          >
            <button
              className="flex w-full items-center justify-between text-left"
              aria-expanded={openIndex === index}
            >
              <h3 className="text-[16px] font-semibold text-slate-900 font-unbounded">
                {item.question}
              </h3>
              <span className={`text-2xl font-bold text-primary transition-transform duration-300 ${openIndex === index ? "rotate-45" : ""}`}>
                +
              </span>
            </button>
            <div
              className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
                openIndex === index ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0 mt-0"
              }`}
            >
              <div className="min-h-0">
                <p className="text-sm leading-7 text-slate-600 font-poppins">
                  {item.answer}
                </p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export const SectionHeading = ({ eyebrow, title, description, align = "left", className = "" }) => (
  <div className={`${align === "center" ? "mx-auto text-center" : "text-left"} max-w-3xl ${className}`}>
    {eyebrow ? (
      <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-2 lg:mb-4 aos-init aos-animate">
        <span className="text-primary" > {eyebrow} </span>
      </h3>
    ) : null}
    <h2 data-aos="fade-up" data-aos-delay="100" className="mt-3 text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
      {title}
    </h2>
    {description ? (
      <p data-aos="fade-up" data-aos-delay="200" className="font-peachi text-sm md:text-base text-lightBlack mb-4 lg:mb-8 aos-init aos-animate mt-4">
        {description}
      </p>
    ) : null}
  </div>
);

export const IconCircle = ({ children, className = "" }) => (
  <span
    className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FF6B0012] text-sm font-semibold text-[#FF6B00] ${className}`}
  >
    {children}
  </span>
);

export const SuggestionCard = ({ label = "Image suggestion", title, text, className = "" }) => (
  <aside className={`rounded-[28px] border border-[#FF6B0020] bg-[#FFF7EF] p-6 shadow-[0_20px_50px_rgba(15,23,42,0.05)] ${className}`}>
    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#FF6B00]">
      {label}
    </p>
    <h3 className="mt-4 text-xl font-semibold text-slate-900 font-unbounded">
      {title}
    </h3>
    <p className="mt-3 text-sm leading-7 text-slate-700 font-poppins">
      {text}
    </p>
  </aside>
);

export const CardGrid = ({ items, columns = "md:grid-cols-2 xl:grid-cols-4", className = "" }) => (
  <div className={`grid gap-5 ${columns} ${className}`}>
    {items.map((item, index) => (
      <article
        key={item.title}
        data-aos="fade-up"
        data-aos-delay={index * 100}
        className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.04)]"
      >
        {item.badge ? <IconCircle>{item.badge}</IconCircle> : null}
        {item.metric ? (
          <p className="mt-4 text-sm font-semibold uppercase tracking-[0.2em] text-[#FF6B00]">
            {item.metric}
          </p>
        ) : null}
        <h3 className="mt-4 text-xl font-semibold text-slate-900 font-unbounded">
          {item.title}
        </h3>
        <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">
          {item.description}
        </p>
      </article>
    ))}
  </div>
);

export const Timeline = ({ steps, columns = "md:grid-cols-2 xl:grid-cols-4" }) => (
  <div className={`grid gap-5 ${columns}`}>
    {steps.map((step, index) => (
      <article
        key={step.title}
        data-aos="fade-up"
        data-aos-delay={index * 100}
        className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.04)]"
      >
        <div className="flex items-center justify-between gap-4">
          <IconCircle>{String(index + 1).padStart(2, "0")}</IconCircle>
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
            {step.label || "Step"}
          </span>
        </div>
        <h3 className="mt-5 text-xl font-semibold text-slate-900 font-unbounded">
          {step.title}
        </h3>
        <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">
          {step.description}
        </p>
      </article>
    ))}
  </div>
);

export const FaqList = ({ faqs }) => {
  const [openFaq, setOpenFaq] = React.useState(0);

  const toggleFaq = (index) => {
    if (!faqs || faqs.length === 0) return;
    if (openFaq === index) {
      // If clicking the currently open one, cycle to next
      setOpenFaq((index + 1) % faqs.length);
    } else {
      // If clicking a closed one, open it
      setOpenFaq(index);
    }
  };

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <article
          key={faq.question}
          data-aos="fade-up"
          data-aos-delay={index * 100}
          className="rounded-[18px] border border-slate-200 bg-white p-4 shadow-[0_20px_50px_rgba(15,23,42,0.04)] cursor-pointer hover:border-primary/20 transition-colors duration-300"
          onClick={() => toggleFaq(index)}
        >
          <button
            className="flex w-full items-center justify-between text-left"
            aria-expanded={openFaq === index}
          >
            <h3 className="text-[16px] font-semibold text-slate-900 font-unbounded">
              {faq.question}
            </h3>
            <span className={`text-2xl font-bold text-primary transition-transform duration-300 ${openFaq === index ? "rotate-45" : ""}`}>
              +
            </span>
          </button>
          <div
            className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
              openFaq === index ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0 mt-0"
            }`}
          >
            <div className="min-h-0">
              <p className="text-sm leading-7 text-slate-600 font-poppins">
                {faq.answer}
              </p>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
};

export const RelatedServices = ({ links, title = "Explore more services", description = "Move between service pages or return to the homepage.", variant = "default" }) => (
  <section className="py-16 md:py-20">
    <div className="mx-auto max-w-7xl px-5 lg:px-16">
      <SectionHeading
        eyebrow="Internal links"
        title={title}
        description={description}
      />
      <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-5">
        {links.map((link, index) => (
          <Link
            key={link.to}
            to={link.to}
            data-aos="fade-up"
            data-aos-delay={index * 100}
            className={`flex flex-col justify-between group bg-white p-6 transition-all duration-300 ${
              variant === "service"
                ? "rounded-[30px] lg:rounded-[40px] border border-slate-100 lg:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(0,0,0,0.1)]"
                : "rounded-[28px] border border-[#FF6B0020] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)]"
            }`}
          >
            <div>
              <p className="text-xs font-semibold uppercase text-[#FF6B00]">
                {link.kicker}
              </p>
              <h3 className={`${variant === "service" ? "mt-4 text-[20px] font-bold" : "mt-4 text-lg font-semibold"} text-slate-900 font-unbounded`}>
                {link.title}
              </h3>
              <p className="mt-3 text-sm leading-7 text-slate-600 font-poppins">
                {link.description}
              </p>
            </div>
            {variant === "service" ? (
              <span className="mt-6 inline-flex items-center justify-center rounded-full bg-[#FF6B00] px-5 py-3 text-white text-sm font-unbounded transition-transform duration-300 group-hover:-translate-y-0.5">
                {link.cta || "View page"}
              </span>
            ) : (
              <span className="mt-5 inline-flex text-sm font-semibold text-[#FF6B00] transition-transform group-hover:translate-x-1">
                {link.cta || "View page"}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const SectionWrap = ({ eyebrow, title, description, image, imageAlt, imagePosition = "right", className = "", children, rightContent }) => {
  const imageElement = image ? (
    <div data-aos={imagePosition === "right" ? "fade-left" : "fade-right"} className="relative">
      <img
        src={image}
        alt={imageAlt}
        className="w-full h-auto rounded-[24px] lg:rounded-[32px] shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
        loading="lazy"
        decoding="async"
      />
    </div>
  ) : null;

  return (
    <section className={`py-16 md:py-20 ${className}`}>
      <div className="mx-auto max-w-7xl px-5 lg:px-16">
        <div className={`grid gap-[50px] lg:grid-cols-2 lg:items-start`}>
          <div className={`${imagePosition === "right" ? "lg:order-1" : "lg:order-2"} space-y-8`}>
            <SectionHeading
              eyebrow={eyebrow}
              title={title}
              description={description}
            />
            {children}
          </div>
          {rightContent ? (
            <div className={`${imagePosition === "right" ? "lg:order-2" : "lg:order-1"}`}>
              {rightContent}
            </div>
          ) : imageElement && (
            <div className={`${imagePosition === "right" ? "lg:order-2" : "lg:order-1"}`}>
              {imageElement}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const ServicePageShell = ({ title, description, canonicalPath, keywords, image, imageAlt, schemas = [], children }) => {
  useEffect(() => {
    const lenis = new Lenis({
      smoothWheel: true,
      smoothTouch: false,
      lerp: 0.05,
    });

    window.lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    window.scrollTo(0, 0);
    lenis.scrollTo(0, { immediate: true });

    // Initialize AOS
    const initAOS = () => {
      AOS.init({
        duration: 900,
        easing: "ease-out-cubic",
        once: true,
        offset: 120,
      });
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(initAOS, { timeout: 1000 });
    } else {
      setTimeout(initAOS, 100);
    }

    return () => {
      lenis.destroy();
      window.lenis = undefined;
    };
  }, [canonicalPath]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SeoHead
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        image={image}
        imageAlt={imageAlt}
        extraMeta={keywords ? [{ name: "keywords", content: keywords }] : []}
        jsonLd={schemas}
      />

      <Header />
      <main className="overflow-x-hidden">{children}</main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServicePageShell;
